import { useState, useCallback, ReactElement, useEffect } from 'react';
import { Button, Card, CardContent } from '@mui/material';
import { Dropdown, ButtonWithDialog, LoadingFragment, LambdaFunctionsTable, PageTitle } from '@localstack/ui';
import { useRoutes, useAwsEffect, useAwsGetter, useAwsKeysMemo } from '@localstack/services';

import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';

import { LambdaProps } from './types';
import { NavTabs } from './components/NavTabs';

export const LambdaFunctions = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const [page, setPage] = useState<number>(0);
  const [paginationKey, setPaginationKey] = useState<string | undefined>(undefined);

  const [selectedFunctions, setSelectedFunctions] = useState<string[]>([]);

  const {
    data: functions,
    isLoading: isLambdasLoading,
    mutate,
  } = useAwsGetter('Lambda', 'listFunctions', [{ Marker: paginationKey }], { clientOverrides });

  const lambdaMarkers = useAwsKeysMemo(page, functions?.NextMarker);

  useEffect(() => {
    setPaginationKey(lambdaMarkers[page]);
  }, [page]);

  const { deleteFunction, isLoading: isFunctionMutating } = useAwsEffect('Lambda', ['deleteFunction'], {
    revalidate: ['listFunctions'],
    clientOverrides,
  });

  const handleDeleteFunctions = useCallback(
    async () => Promise.all(selectedFunctions.map((FunctionName) => deleteFunction({ FunctionName }))),
    [selectedFunctions],
  );

  return (
    <>
      <Layout
        documentTitle="Lambda: Event Functions"
        title={
          <PageTitle
            title="Lambda Event Functions"
            onMutate={mutate}
            breadcrumbs={[
              ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
              ['Event Functions', null],
            ]}
          />
        }
        tabs={<NavTabs routes={routes} />}
        actions={
          <>
            <Button variant="outlined" onClick={() => goto(routes.RESOURCES_LAMBDA_FUNCTION_NEW)}>
              Create
            </Button>
            <Dropdown label="Actions">
              <ButtonWithDialog
                componentType="MenuItem"
                disabled={selectedFunctions.length === 0 || isFunctionMutating}
                dialogTitle={`Remove ${selectedFunctions.length} event function(s)?`}
                onClick={handleDeleteFunctions}
                dialogContent="Selected Event Functions will be permanently deleted"
              >
                Remove Selected
              </ButtonWithDialog>
            </Dropdown>
          </>
        }
      >
        <Card>
          <CardContent>
            <LoadingFragment
              loading={isLambdasLoading}
              arrayData={functions?.Functions ?? []}
              variant="list"
              height={50}
              size={10}
            >
              <LambdaFunctionsTable
                loading={isLambdasLoading}
                functions={functions?.Functions ?? []}
                onSelectFunctions={setSelectedFunctions}
                onViewFunction={(name) => goto(routes.RESOURCES_LAMBDA_FUNCTION, { name })}
                page={page}
                onPageChange={setPage}
                pageSize={LAMBDA_DEFAULT_PAGE_SIZE}
                hasMore={!!functions?.NextMarker}
              />
            </LoadingFragment>
          </CardContent>
        </Card>
      </Layout>
    </>
  );
};
